// The player notebook (issue 1166; decision D on #1062): a person's own pages at a table, and the DM's read of the
// pages players shared. Every call goes to this origin through `requestJson` (so `authFetch`); nothing here rides the
// socket. The one socket word is the DM's `table:notebook` (no page in it), handed to the open view by `notebookChanged`.
import { type NotebookPage, type NotebookPatch, type SharedNotebook } from '@dragons-table/vtt-shared';
import { requestJson } from './api';

export type Answer<T> = { kind: 'ok'; value: T } | { kind: 'refused'; reason: string } | { kind: 'error' };

const enc = encodeURIComponent;
const DOWN = "The notebook can't be reached just now. Try again in a moment.";
const reasonOf = (status: number, body: unknown, fallback: string): Answer<never> => {
  if (status === 0) return { kind: 'error' };
  const r = (body as { reason?: unknown } | null)?.reason;
  return { kind: 'refused', reason: typeof r === 'string' ? r : fallback };
};
const base = (campaignId: string) => `/api/tables/${enc(campaignId)}/notebook`;

/** Your own pages at this table, in order. */
export async function myPages(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Answer<NotebookPage[]>> {
  const { status, body } = await requestJson(base(campaignId), {}, fetchImpl);
  const pages = (body as { pages?: unknown } | null)?.pages;
  return status === 200 && Array.isArray(pages) ? { kind: 'ok', value: pages as NotebookPage[] } : reasonOf(status, body, DOWN);
}

export async function newPage(campaignId: string, title: string, fetchImpl: typeof fetch = fetch): Promise<Answer<NotebookPage>> {
  const { status, body } = await requestJson(base(campaignId), { method: 'POST', body: JSON.stringify({ title }) }, fetchImpl);
  return status === 201 && body ? { kind: 'ok', value: body as NotebookPage } : reasonOf(status, body, DOWN);
}

/** Change a page: any of its title, body, shared switch and place. */
export async function savePage(campaignId: string, id: string, patch: NotebookPatch, fetchImpl: typeof fetch = fetch): Promise<Answer<NotebookPage>> {
  const { status, body } = await requestJson(`${base(campaignId)}/${enc(id)}`, { method: 'PUT', body: JSON.stringify(patch) }, fetchImpl);
  if (status === 200 && body) return { kind: 'ok', value: body as NotebookPage };
  if (status === 400) return { kind: 'refused', reason: 'A page needs a title of one line, up to 120 characters, and at most 20,000 characters of text.' };
  if (status === 404) return { kind: 'refused', reason: 'That page is no longer in your notebook.' };
  return reasonOf(status, body, DOWN);
}

export async function deletePage(campaignId: string, id: string, fetchImpl: typeof fetch = fetch): Promise<Answer<null>> {
  const { status, body } = await requestJson(`${base(campaignId)}/${enc(id)}`, { method: 'DELETE' }, fetchImpl);
  return status === 204 || status === 404 ? { kind: 'ok', value: null } : reasonOf(status, body, DOWN);
}

/** The DM's: each current player's shared pages. */
export async function sharedWithMe(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Answer<SharedNotebook[]>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/notebooks`, {}, fetchImpl);
  const players = (body as { players?: unknown } | null)?.players;
  return status === 200 && Array.isArray(players) ? { kind: 'ok', value: players as SharedNotebook[] } : reasonOf(status, body, DOWN);
}

// ── The DM's live word ────────────────────────────────────────────────────
type Listener = (campaignId: string) => void;
const listeners = new Set<Listener>();
/** Called when the socket says a player's shared pages changed at this table. */
export function notebookChanged(campaignId: string): void { for (const l of listeners) l(campaignId); }
/** Listen for that word; the returned function stops listening. */
export function onNotebookChanged(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; }
